import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    booleanAttribute,
    Component,
    computed,
    contentChild,
    input,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import {
    resolveColorContext,
    type ContextFamily,
    type ContextTone,
} from '@quipex/shared/utils';
import { QpxIcon, type IconSetAlias } from '../../directives';
import { QpxPillLabel } from './pill-label';

type IconOptions = {
    name: string;
    set: IconSetAlias;
};
type IconInput = string | Partial<IconOptions>;
const defaultIcon: IconOptions = {
    name: '',
    set: 'sym-outlined',
};

@Component({
    selector: 'qpx-pill, button[qpx-pill]',
    imports: [NgIcon, QpxIcon, NgTemplateOutlet],
    templateUrl: './pill.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': 'className()',
        '[attr.role]': "ariaRole() === 'status' ? 'status' : null",
        '[style.--qpx-pill-background-color]': 'backgroundColor()',
        '[style.--qpx-pill-base-surface]': 'backgroundColor()',
    },
})
export class QpxPill {
    //#region Inputs
    public readonly label = input('');

    public readonly truncate = input(false);

    public readonly darkenBackground = input(false);

    public readonly lightenBackground = input(false);

    public readonly darkenIconBackground = input(false);

    public readonly count = input(0);

    public readonly iconOnly = input(false);

    public readonly icon = input<IconOptions, IconInput>(defaultIcon, {
        transform: (value) => {
            if (typeof value === 'string') {
                return { ...defaultIcon, name: value };
            }
            return { ...defaultIcon, ...value };
        },
    });

    public readonly position = input<'start' | 'end'>('start');

    public readonly size = input<'sm' | 'md'>('md');

    public readonly context = input<ContextFamily>('primary');
    public readonly tone = input<ContextTone>('default');
    public readonly decorative = input<boolean | 'icon', unknown>(false, {
        transform: (value) =>
            value === 'icon' ? 'icon' : booleanAttribute(value),
    });

    public readonly ariaRole = input<'decorative' | 'status'>('decorative');
    //#endregion

    //#region Template references
    protected readonly content = contentChild('custom');
    protected readonly projectedLabel = contentChild(QpxPillLabel);
    //#endregion

    //#region State
    protected readonly className = computed(() => {
        const colors = resolveColorContext(this.context(), this.tone());
        const foregroundClass =
            this.decorative() === true
                ? colors.decorative.className
                : colors.foreground.className;
        const _className = [
            'qpx-pill',
            this.size(),
            colors.surface.className,
            foregroundClass,
        ];

        if (this.darkenBackground()) {
            _className.push('darken-background');
        } else if (this.lightenBackground()) {
            _className.push('lighten-background');
        }

        if (this.truncate()) {
            _className.push('truncate-text');
        }

        if (this.iconOnly()) {
            _className.push('icon-only');
        }

        if (this.darkenIconBackground()) {
            _className.push('darken-icon-background');
        }

        return _className.join(' ');
    });

    protected readonly backgroundColor = computed(() => {
        return `var(${resolveColorContext(this.context(), this.tone()).surface.variable})`;
    });
    protected readonly hasContent = computed(() => !!this.content());
    protected readonly iconName = computed(() => this.icon().name);
    protected readonly hasIcon = computed(() => !!this.iconName());
    protected readonly iconClassName = computed(() => {
        const colors = resolveColorContext(this.context(), this.tone());
        const foregroundClass = this.decorative()
            ? colors.decorative.className
            : colors.foreground.className;

        return `icon ${foregroundClass}`;
    });
    protected readonly iconSize = computed(() =>
        this.size() === 'sm' ? 'xs' : 'sm'
    );
    //#endregion
}
